import React, { useEffect } from "react";
import PrimaryButton from "./../components/PrimaryButton";
import { useGlideDepositConfig } from "./../lib/useDepositConfig";
import { ArrowUpDown } from "lucide-react";
import Warning from "../components/Warning";
import { useConnectedWallet } from "../lib/useConnectedWallet";
import { useBalance, useReadContract } from "wagmi";
import { erc20Abi, formatUnits, isHex, parseUnits } from "viem";
import { formatDisplayAmount } from "../lib/formatDisplayAmount";
import { formatShortWalletAddress } from "../lib/formatShortWalletAddress";
import ModalBackButton from "../components/ModalBackButton";
import { normalizeDecimalInput } from "../lib/normalizeDecimalInput";
import {
  Chain,
  Currency,
  getEIP155ChainId,
  makeRequest,
  PaymentOption,
} from "@paywithglide/glide-js";
import { Connection, PublicKey } from "@solana/web3.js";
import { ButtonWithIconSubtitleAndAccessory } from "../components/ButtonWithIconSubtitleAndAccessory";
import { LogoWithBadge } from "../components/LogoWithBadge";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import CoinbaseLogo from "../logos/CoinbaseLogo";
import MoonPayLogo from "../logos/MoonPayLogo";
import { InteracLogo } from "../logos/InteracLogo";
import VisaMastercardLogo from "../logos/VisaMastercardLogo";
import { PaymentMethod } from "./FundingSource";
import { minUSDAmountByPaymentMethod } from "../lib/minUSDAmountByPaymentMethod";
import { getTokenConversionMessage } from "../lib/getTokenConversionMessage";
import { logger } from "../lib/logger";
import { listSupportedCurrencies } from "@paywithglide/glide-js";

type Props = {
  isCreatingSession: boolean;
  isOverCapacity: boolean;
  isInsufficientPaymentAmount: boolean;
  paymentMethod: PaymentMethod;
  withdrawChain?: Chain;
  selectedWithdrawCurrency?: Currency;
  paymentOption?: PaymentOption;
  depositAmount: string;
  onChangeDepositAmount: (amount: string) => void;
  depositAmountDenomination: "token" | "usd";
  onChangeDepositAmountDenomination: (denomination: "token" | "usd") => void;
  onChangePaymentOptions: () => void;
  onChangeWithdrawCurrency: () => void;
  onUpdateDepositAmount: () => void;
  onContinue: (p: {
    depositAmount: string;
    transactionAmountUSD?: string;
    depositAmountDenomination: "token" | "usd";
  }) => void;
  onBack?: () => void;
};

export default function DepositAmount({
  isCreatingSession,
  isOverCapacity,
  isInsufficientPaymentAmount,
  paymentMethod,
  withdrawChain,
  selectedWithdrawCurrency,
  paymentOption,
  depositAmount,
  onChangeDepositAmount,
  depositAmountDenomination,
  onChangeDepositAmountDenomination,
  onChangePaymentOptions,
  onChangeWithdrawCurrency,
  onUpdateDepositAmount,
  onContinue,
  onBack,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();

  // Helper to determine if we should use pay mode logic
  const usePayModeBehavior =
    depositConfig.mode === "pay" ||
    depositConfig.mode === "call" ||
    (depositConfig.mode === "deposit" && depositConfig.hideSettlementCopy);

  const { address } = useConnectedWallet();

  const currencyContractAddress = depositConfig.currency!.contractAddressOn?.(
    depositConfig.chain!,
  );

  const { data: gasBalance, isSuccess: didLoadGasBalance } = useBalance({
    chainId: depositConfig.chain?.id.includes("eip155")
      ? getEIP155ChainId(depositConfig.chain.id)
      : undefined,
    address: isHex(address) ? address : undefined,
  });

  const { data: tokenBalance, isSuccess: didLoadTokenBalance } =
    useReadContract({
      chainId: depositConfig.chain?.id.includes("eip155")
        ? getEIP155ChainId(depositConfig.chain.id)
        : undefined,
      address: isHex(currencyContractAddress) ? currencyContractAddress : "0x0",
      abi: erc20Abi,
      functionName: "balanceOf",
      args: isHex(address) ? [address] : undefined,
    });

  // Solana balance fetching
  const isSolanaChain = depositConfig.chain?.id.startsWith("solana:");
  const [solanaBalance, setSolanaBalance] = React.useState<bigint | null>(null);
  const [didLoadSolanaBalance, setDidLoadSolanaBalance] = React.useState(false);

  useEffect(() => {
    if (!isSolanaChain || !address) {
      setSolanaBalance(null);
      setDidLoadSolanaBalance(false);
      return;
    }

    const fetchSolanaBalance = async () => {
      try {
        const isDevnet = depositConfig.chain?.id === "solana:103";
        const rpcUrl = isDevnet
          ? "https://devnet.helius-rpc.com/?api-key=a1c51ad4-9fbf-43d0-b129-1cf9ec6f2120"
          : "https://mainnet.helius-rpc.com/?api-key=a1c51ad4-9fbf-43d0-b129-1cf9ec6f2120";

        const connection = new Connection(rpcUrl, "confirmed");
        const publicKey = new PublicKey(address);

        if (currencyContractAddress) {
          // SPL token balance
          const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
            publicKey,
            { mint: new PublicKey(currencyContractAddress) },
          );

          if (tokenAccounts.value.length > 0) {
            const amount =
              tokenAccounts.value[0].account.data.parsed.info.tokenAmount
                .amount;
            setSolanaBalance(BigInt(amount));
          } else {
            setSolanaBalance(0n);
          }
        } else {
          // Native SOL balance
          const lamports = await connection.getBalance(publicKey);
          setSolanaBalance(BigInt(lamports));
        }

        setDidLoadSolanaBalance(true);
      } catch (error) {
        logger.error(`Error fetching Solana balance: ${error}`);
        setSolanaBalance(null);
        setDidLoadSolanaBalance(false);
      }
    };

    fetchSolanaBalance();
  }, [
    isSolanaChain,
    address,
    currencyContractAddress,
    depositConfig.chain?.id,
  ]);

  // Unified balance based on chain type
  const balance = isSolanaChain
    ? solanaBalance
    : depositConfig.currency?.contractAddressOn
      ? tokenBalance
      : gasBalance?.value;
  const didLoadBalance = isSolanaChain
    ? didLoadSolanaBalance
    : depositConfig.currency?.contractAddressOn
      ? didLoadTokenBalance
      : didLoadGasBalance;

  const depositAmountInputRef = React.useRef<HTMLInputElement>(null);
  const [conversionAmount, setConversionAmount] = React.useState("");
  const [transactionAmountUSD, setTransactionAmountUSD] =
    React.useState<string>();
  const [currencyPrice, setCurrencyPrice] = React.useState<string>();
  const [isUpdatingConversionAmount, setIsUpdatingConversionAmount] =
    React.useState(false);
  const [paymentCurrencyDecimals, setPaymentCurrencyDecimals] =
    React.useState<number>(18);

  // Fetch currency decimals when payment option changes
  useEffect(() => {
    // Function to get currency decimals from payment option's currency ID
    const getCurrencyDecimals = async (currencyId: string): Promise<number> => {
      try {
        // Extract chain ID from currency ID (format: "eip155:8453/slip44:0x...")
        const parts = currencyId.split(":");
        const chainId =
          `${parts[0]}:${parts[1].split("/")[0]}` as `eip155:${number}`;

        const currencies = await listSupportedCurrencies(glideConfig, {
          chainId: chainId,
          all: true,
        });

        // Find the currency that matches the currency ID
        const currency = currencies.find(
          (c) => c.on({ id: chainId } as Chain) === currencyId,
        );

        return currency?.decimals || 18; // Default to 18 if not found
      } catch (error) {
        logger.error(`Error fetching currency decimals: ${error}`);
        return 18; // Default to 18 decimals
      }
    };

    if (usePayModeBehavior && paymentOption) {
      getCurrencyDecimals(paymentOption.paymentCurrency).then(
        setPaymentCurrencyDecimals,
      );
    }
  }, [usePayModeBehavior, paymentOption, glideConfig]);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        setIsUpdatingConversionAmount(true);

        // For pay/call modes with payment option, use the payment currency for price fetching
        let currencyId;
        if (usePayModeBehavior && paymentOption) {
          // Use the payment currency ID from the payment option
          currencyId = paymentOption.paymentCurrency;
        } else {
          // Use the target currency for other modes
          currencyId = depositConfig.currency!.on(depositConfig.chain!);
        }

        const { price } = await makeRequest<{ price: string }>(glideConfig, {
          url: "/marketdata/price",
          queryParams: {
            currency: currencyId,
          },
        });
        setCurrencyPrice(price);
        setIsUpdatingConversionAmount(false);
      } catch (e) {
        logger.error(`${e}`);
      }
    };

    fetchPrice();
    depositAmountInputRef.current?.focus();
  }, [
    depositConfig.chain,
    depositConfig.currency,
    usePayModeBehavior,
    paymentOption,
    glideConfig,
  ]);

  const updateConversionAmount = React.useCallback(() => {
    onUpdateDepositAmount();

    const amount = Number(depositAmount.replace(/^\$/, ""));

    if (!amount || !currencyPrice) {
      setConversionAmount("");
      return;
    }

    // Get the correct currency decimals for the conversion
    const currencyDecimals =
      usePayModeBehavior && paymentOption
        ? paymentCurrencyDecimals
        : depositConfig.currency!.decimals;

    if (depositAmountDenomination === "usd") {
      setConversionAmount(
        (amount / Number(currencyPrice)).toFixed(currencyDecimals),
      );
      setTransactionAmountUSD(amount.toFixed(2));
    } else {
      setConversionAmount((amount * Number(currencyPrice)).toFixed(2));
      setTransactionAmountUSD((amount * Number(currencyPrice)).toFixed(2));
    }
  }, [
    depositAmount,
    depositAmountDenomination,
    currencyPrice,
    depositConfig.currency,
    paymentOption,
    paymentCurrencyDecimals,
    onUpdateDepositAmount,
    usePayModeBehavior,
  ]);

  useEffect(() => {
    updateConversionAmount();
  }, [depositAmount, updateConversionAmount]);

  const handleContinue = (params: {
    depositAmount: string;
    transactionAmountUSD?: string;
    depositAmountDenomination: "token" | "usd";
  }) => {
    onContinue(params);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^0-9.]/g, "");

    if (input === "") {
      onChangeDepositAmount("");
      return;
    }

    // Normalize decimal input (e.g., ".5" becomes "0.5")
    input = normalizeDecimalInput(input);

    // Validate decimal number format
    const numberFormat =
      depositAmountDenomination === "token"
        ? /^\d*\.?\d{0,4}$/
        : /^\d*\.?\d{0,2}$/;
    if (!numberFormat.test(input)) {
      return;
    }

    const number = parseFloat(input);
    if (isNaN(number)) {
      onChangeDepositAmount("");
    } else {
      if (depositAmountDenomination === "usd" && !input.startsWith("$")) {
        input = `$${input}`;
      }
      onChangeDepositAmount(input);
    }
  };

  const getInputWidth = () => {
    const baseLength =
      depositAmount.length || (depositAmountDenomination === "token" ? 1 : 2);
    return `${baseLength}ch`;
  };

  const usdDepositAmount = Number(
    depositAmountDenomination === "usd"
      ? depositAmount.replace(/^\$/, "")
      : conversionAmount,
  );

  const isBelowMinAmount =
    paymentMethod === "debit_card_alias"
      ? usdDepositAmount <
        minUSDAmountByPaymentMethod[depositConfig.debitCardAlias || "fiat"]
      : paymentMethod === "interac" && depositConfig.mode === "withdraw"
        ? usdDepositAmount < 1
        : usdDepositAmount < minUSDAmountByPaymentMethod[paymentMethod];

  const withdrawOverBalance =
    didLoadBalance && depositConfig.mode === "withdraw"
      ? parseUnits(depositAmount, depositConfig.currency!.decimals) >
        (balance || 0n)
      : false;

  const insufficientBalance =
    paymentMethod === "wallet" || paymentMethod === "coinbase_app"
      ? paymentOption
        ? Number(paymentOption.balanceUSD) <
          usdDepositAmount + Number(paymentOption.totalFeeUSD || 0)
        : false
      : false;

  const isLoading = isCreatingSession || isUpdatingConversionAmount;
  const isDisabled =
    isLoading ||
    Number(depositAmount) <= 0 ||
    isOverCapacity ||
    isInsufficientPaymentAmount ||
    insufficientBalance ||
    isBelowMinAmount ||
    withdrawOverBalance;

  const tokenConversionMessage =
    depositConfig.mode === "withdraw" &&
    selectedWithdrawCurrency &&
    withdrawChain
      ? getTokenConversionMessage({
          from: {
            tokenSymbol: depositConfig.currency!.symbol,
            chainId: depositConfig.chain!.id,
            chainName: depositConfig.chain!.name,
          },
          to: {
            tokenSymbol: selectedWithdrawCurrency.symbol,
            chainId: withdrawChain.id,
            chainName: withdrawChain.name,
          },
        })
      : paymentOption &&
          depositConfig.mode !== "pay" &&
          depositConfig.mode !== "call"
        ? getTokenConversionMessage({
            from: {
              tokenSymbol: paymentOption.currencySymbol,
              chainId: paymentOption.chainId,
              chainName: paymentOption.chainName,
            },
            to: {
              tokenSymbol: depositConfig.currency!.symbol,
              chainId: depositConfig.chain!.id,
              chainName: depositConfig.chain!.name,
            },
          })
        : "";

  return (
    <>
      {onBack ? <ModalBackButton onClick={onBack} /> : null}

      <div className="">
        {depositConfig.mode === "buy" ? (
          <TransactionCheckoutHeader
            title={`Buy ${depositConfig.currency!.symbol}`}
            subtitle="How much would you like to buy?"
          />
        ) : depositConfig.mode === "deposit" ? (
          <TransactionCheckoutHeader
            title={
              depositConfig.hideSettlementCopy &&
              (paymentMethod === "coinbase" ||
                paymentMethod === "fiat" ||
                paymentMethod === "interac" ||
                paymentMethod === "debit_card_alias")
                ? paymentMethod === "coinbase"
                  ? "Coinbase"
                  : paymentMethod === "fiat"
                    ? "MoonPay"
                    : paymentMethod === "interac"
                      ? "Interac"
                      : "Debit card"
                : paymentMethod === "coinbase" ||
                    paymentMethod === "fiat" ||
                    paymentMethod === "interac" ||
                    paymentMethod === "debit_card_alias"
                  ? `Buy ${depositConfig.currency!.symbol}`
                  : depositConfig.hideSettlementCopy && paymentOption
                    ? `Deposit with ${paymentOption.currencySymbol}`
                    : `Deposit ${depositConfig.currency!.symbol}`
            }
            subtitle={
              paymentMethod === "coinbase" ||
              paymentMethod === "fiat" ||
              paymentMethod === "interac" ||
              paymentMethod === "debit_card_alias"
                ? depositConfig.hideSettlementCopy
                  ? "How much would you like to deposit?"
                  : "How much would you like to buy?"
                : "How much would you like to deposit?"
            }
          />
        ) : depositConfig.mode === "withdraw" ? (
          <TransactionCheckoutHeader
            title={`Withdraw ${depositConfig.currency!.symbol}`}
            subtitle="How much would you like to withdraw?"
          />
        ) : depositConfig.mode === "pay" ? (
          <TransactionCheckoutHeader
            title={`Pay with ${paymentOption?.currencyName || depositConfig.currency!.symbol}`}
            subtitle="How much would you like to pay?"
          />
        ) : depositConfig.mode === "call" ? (
          <TransactionCheckoutHeader
            title={`Pay with ${paymentOption?.currencyName || depositConfig.currency!.symbol}`}
            subtitle="How much would you like to pay?"
          />
        ) : null}

        <div className="flex justify-center items-baseline mt-8 gap-1">
          <input
            className={`font-mono text-[--color-text-primary] bg-transparent text-center font-medium focus:outline-none caret-neutral-400 ${
              depositAmount.length > 18
                ? ""
                : depositAmount.length > 14
                  ? "text-lg"
                  : depositAmount.length > 10
                    ? "text-2xl"
                    : depositAmount.length > 8
                      ? "text-3xl"
                      : depositAmount.length > 4
                        ? "text-5xl"
                        : "text-6xl"
            }`}
            style={{
              width: getInputWidth(),
              marginLeft:
                depositAmountDenomination === "token"
                  ? depositConfig.currency!.symbol.length * 10 + "px"
                  : "0",
            }}
            placeholder={depositAmountDenomination === "token" ? "0" : "$0"}
            value={
              depositAmountDenomination === "token"
                ? depositAmount.replace(/^\$/, "")
                : depositAmount || ""
            }
            onChange={handleChange}
            ref={depositAmountInputRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isDisabled) {
                handleContinue({
                  depositAmount:
                    depositAmountDenomination === "token"
                      ? depositAmount.replace(/^\$/, "")
                      : conversionAmount,
                  transactionAmountUSD,
                  depositAmountDenomination,
                });
              }
            }}
            inputMode="decimal"
          />
          {depositAmountDenomination === "token" ? (
            <div className="font-medium text-sm text-[--color-text-secondary]">
              {usePayModeBehavior && paymentOption
                ? paymentOption.currencySymbol
                : depositConfig.currency!.symbol}
            </div>
          ) : null}
        </div>

        {(depositConfig.mode === "deposit" ||
          depositConfig.mode === "buy" ||
          usePayModeBehavior) &&
        !(
          depositConfig.hideSettlementCopy &&
          (paymentMethod === "coinbase" ||
            paymentMethod === "fiat" ||
            paymentMethod === "interac")
        ) ? (
          <button
            style={{
              borderWidth: "var(--alert-border-width)",
              borderColor: "var(--color-alert-border)",
            }}
            className={`mx-auto mt-2 flex items-center gap-1 bg-[--color-alert-background] px-3 py-1 rounded-lg text-[--color-text-secondary] text-sm hover:opacity-80 transition-opacity ${
              isUpdatingConversionAmount ? "animate-pulse cursor-wait" : ""
            }`}
            onClick={() => {
              if (depositAmountDenomination === "token") {
                if (Number(transactionAmountUSD) > 0.01) {
                  onChangeDepositAmount(
                    `$${Number(transactionAmountUSD).toFixed(2)}`,
                  );
                } else {
                  onChangeDepositAmount("");
                  depositAmountInputRef.current?.focus();
                }
              } else if (Number(conversionAmount) > 0.0001) {
                onChangeDepositAmount(Number(conversionAmount).toFixed(4));
              } else {
                onChangeDepositAmount("");
                depositAmountInputRef.current?.focus();
              }

              onChangeDepositAmountDenomination(
                depositAmountDenomination === "token" ? "usd" : "token",
              );
            }}
          >
            <ArrowUpDown className="h-4 w-4" />{" "}
            {depositAmountDenomination === "token" ? "$" : ""}
            {isUpdatingConversionAmount
              ? "—"
              : depositAmountDenomination === "token"
                ? `${
                    Number(transactionAmountUSD)
                      ? Number(transactionAmountUSD).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"
                  }`
                : Number(conversionAmount)
                  ? formatDisplayAmount(conversionAmount)
                  : "—"}{" "}
            {depositAmountDenomination === "token"
              ? ""
              : usePayModeBehavior && paymentOption
                ? paymentOption.currencySymbol
                : depositConfig.currency!.symbol}
          </button>
        ) : null}

        {depositConfig.mode === "withdraw" ? (
          <div className="mt-2 mb-8 flex flex-col items-center">
            {didLoadBalance ? (
              <div className="flex items-center justify-center gap-1 text-sm text-[--color-text-secondary]">
                {formatDisplayAmount(
                  formatUnits(balance || 0n, depositConfig.currency!.decimals),
                )}{" "}
                {depositConfig.currency!.symbol} available on{" "}
                {depositConfig.chain!.name}
              </div>
            ) : (
              <div className="animate-pulse bg-[--color-background-secondary] h-5 w-32 opacity-20"></div>
            )}
          </div>
        ) : (
          <div className="mt-8" />
        )}

        <div>
          <div className="text-xs text-[--color-text-secondary]">
            {depositConfig.mode === "withdraw"
              ? paymentMethod === "interac"
                ? "You'll receive CAD via"
                : "You'll receive"
              : "Paying with"}
          </div>
          {paymentMethod === "coinbase" ? (
            <ButtonWithIconSubtitleAndAccessory
              icon={<CoinbaseLogo className="h-6 w-6 rounded-full" />}
              title={"Coinbase"}
            />
          ) : paymentMethod === "fiat" ? (
            <ButtonWithIconSubtitleAndAccessory
              icon={<MoonPayLogo className="h-6 w-6" />}
              title={"MoonPay"}
            />
          ) : paymentMethod === "interac" ? (
            <ButtonWithIconSubtitleAndAccessory
              icon={<InteracLogo className="h-6 w-6" />}
              title={"Interac"}
            />
          ) : paymentMethod === "debit_card_alias" ? (
            <ButtonWithIconSubtitleAndAccessory
              icon={<VisaMastercardLogo className="h-6 w-6" />}
              title={"Debit card"}
            />
          ) : null}

          {depositConfig.mode === "withdraw" &&
          selectedWithdrawCurrency &&
          withdrawChain ? (
            <ButtonWithIconSubtitleAndAccessory
              icon={
                <LogoWithBadge
                  logoUrl={selectedWithdrawCurrency.logoUrl || ""}
                  badgeUrl={withdrawChain.logoUrl}
                />
              }
              title={selectedWithdrawCurrency.name}
              onClick={() => onChangeWithdrawCurrency()}
              hasMoreOptions
            />
          ) : null}

          {(paymentMethod === "wallet" ||
            paymentMethod === "transfer" ||
            paymentMethod === "coinbase_app") &&
          paymentOption ? (
            <ButtonWithIconSubtitleAndAccessory
              icon={
                <LogoWithBadge
                  logoUrl={paymentOption.currencyLogoUrl}
                  badgeUrl={paymentOption.chainLogoUrl}
                />
              }
              title={paymentOption.currencyName}
              subtitle={
                (depositConfig.mode === "deposit" ||
                  depositConfig.mode === "pay" ||
                  depositConfig.mode === "call") &&
                (paymentMethod === "wallet" || paymentMethod === "coinbase_app")
                  ? `$${Number(paymentOption.balanceUSD).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )} from ${address ? formatShortWalletAddress(address) : "wallet"}`
                  : ""
              }
              onClick={() => onChangePaymentOptions()}
              hasMoreOptions
            />
          ) : null}

          {tokenConversionMessage && !depositConfig.hideSettlementCopy ? (
            <div className="mt-2 text-xs text-[--color-text-secondary]">
              {tokenConversionMessage}
            </div>
          ) : null}
        </div>

        {depositConfig.chain?.id === "hypercore:mainnet" ? (
          <Warning className="mt-4">
            If you've never deposited to this Hyperliquid account before, a
            one-time fee of 1 USDC will be deducted.
          </Warning>
        ) : null}

        {isOverCapacity ? (
          <Warning className="mt-4">
            We're over capacity at this time. Try a lower amount or come back
            later.
          </Warning>
        ) : null}

        {isInsufficientPaymentAmount ? (
          <Warning className="mt-4">
            The amount is too low for this transaction. Please try a higher
            amount.
          </Warning>
        ) : null}

        <PrimaryButton
          loading={isLoading}
          disabled={isDisabled}
          className="mt-4"
          onClick={() => {
            onContinue({
              depositAmount:
                depositAmountDenomination === "token"
                  ? depositAmount.replace(/^\$/, "")
                  : conversionAmount,
              transactionAmountUSD,
              depositAmountDenomination,
            });
          }}
        >
          {Number(depositAmount) <= 0
            ? "Enter an amount"
            : insufficientBalance || withdrawOverBalance
              ? "Insufficient balance"
              : isBelowMinAmount
                ? `Min amount $${
                    paymentMethod === "interac" &&
                    depositConfig.mode === "withdraw"
                      ? 1
                      : minUSDAmountByPaymentMethod[
                          paymentMethod === "debit_card_alias"
                            ? depositConfig.debitCardAlias || "fiat"
                            : paymentMethod
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                  }`
                : "Continue"}
        </PrimaryButton>

        {depositConfig.mode === "buy" ||
        paymentMethod === "coinbase" ||
        paymentMethod === "fiat" ? (
          <div className="mt-2 text-center">
            <p className="text-xs text-[--color-text-secondary] italic">
              Additional fees may apply
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
}
